import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { PageLayout } from '../components/common/PageLayout';
import { DataTable } from '../components/common/DataTable';
import { StatusBadge } from '../components/common/StatusBadge';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import {
  useRunDetails,
  useAccountResults,
  StepResult,
  AccountResult,
  downloadExcelExport,
  downloadCollectionFile,
} from '../hooks/useCaapApi';

const stepColumns: ColumnDef<StepResult, any>[] = [
  { accessorKey: 'StepNumber', header: 'Step #', size: 70 },
  { accessorKey: 'StepName', header: 'Name' },
  {
    accessorKey: 'Status',
    header: 'Status',
    cell: ({ getValue }) => <StatusBadge status={getValue()} />,
  },
  { accessorKey: 'AccountCount', header: 'Accounts' },
  { accessorKey: 'AssignedCount', header: 'Assigned' },
  {
    accessorKey: 'TotalPayoffBalance',
    header: 'Payoff Balance',
    cell: ({ getValue }) => {
      const val = getValue() as number;
      return val != null ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '\u2014';
    },
  },
  {
    accessorKey: 'DurationSeconds',
    header: 'Duration',
    cell: ({ getValue }) => {
      const s = getValue() as number | null;
      if (s == null) return '\u2014';
      return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
    },
  },
];

const accountColumns: ColumnDef<AccountResult, any>[] = [
  { accessorKey: 'AccountNumber', header: 'Account #' },
  { accessorKey: 'StudentName', header: 'Student' },
  { accessorKey: 'SignerName', header: 'Signer' },
  { accessorKey: 'SchoolName', header: 'School' },
  { accessorKey: 'AccountStatus', header: 'Status' },
  { accessorKey: 'AccountCondition', header: 'Condition' },
  { accessorKey: 'StudentStatus', header: 'Student Status' },
  {
    accessorKey: 'CurrentBalance',
    header: 'Current Bal',
    cell: ({ getValue }) => {
      const val = getValue() as number | null;
      return val != null ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '\u2014';
    },
  },
  {
    accessorKey: 'PayoffBalance',
    header: 'Payoff Bal',
    cell: ({ getValue }) => {
      const val = getValue() as number | null;
      return val != null ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '\u2014';
    },
  },
  {
    accessorKey: 'LastPaymentDate',
    header: 'Last Payment',
    cell: ({ getValue }) => {
      const val = getValue() as string | null;
      return val ? new Date(val).toLocaleDateString() : '\u2014';
    },
  },
  {
    accessorKey: 'WasAssigned',
    header: 'Assigned',
    cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
  },
];

export const RunDetail: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const id = parseInt(runId || '0', 10);
  const { data, isLoading, error } = useRunDetails(id);
  const [expandedStep, setExpandedStep] = useState<number | null>(null);

  const { data: accounts, isLoading: accountsLoading } = useAccountResults(
    id,
    expandedStep ?? 0
  );

  const hasStep7 = data?.steps?.some((s) => s.StepNumber === 7 && s.AccountCount > 0);

  return (
    <PageLayout>
      <div className="mb-4">
        <Link to="/caap" className="text-sm text-blue-600 hover:text-blue-800">
          &larr; Back to Run History
        </Link>
      </div>

      {isLoading && <LoadingSpinner message="Loading run details..." />}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-950 p-4 text-sm text-red-700 dark:text-red-300">
          Failed to load run: {(error as Error).message}
        </div>
      )}

      {data && (
        <>
          {/* Run Summary Header */}
          <div className="mb-6 rounded-lg border border-border-default bg-surface-alt p-5">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-primary">Run #{data.run.RunID}</h1>
                <p className="mt-1 text-sm text-muted">
                  {new Date(data.run.RunDate).toLocaleString()} &middot;{' '}
                  {data.run.RunType} &middot; {data.run.ScheduleType}
                </p>
              </div>
              <StatusBadge status={data.run.Status} />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <p className="text-xs font-medium uppercase text-muted">Accounts Found</p>
                <p className="text-lg font-semibold text-primary">
                  {data.run.TotalAccountsFound?.toLocaleString() ?? '\u2014'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-muted">Accounts Assigned</p>
                <p className="text-lg font-semibold text-primary">
                  {data.run.TotalAccountsAssigned?.toLocaleString() ?? '\u2014'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-muted">Payoff Balance</p>
                <p className="text-lg font-semibold text-primary">
                  {data.run.TotalPayoffBalance != null
                    ? `$${data.run.TotalPayoffBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                    : '\u2014'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-muted">Duration</p>
                <p className="text-lg font-semibold text-primary">
                  {data.run.DurationSeconds != null
                    ? data.run.DurationSeconds < 60
                      ? `${data.run.DurationSeconds}s`
                      : `${Math.floor(data.run.DurationSeconds / 60)}m ${data.run.DurationSeconds % 60}s`
                    : '\u2014'}
                </p>
              </div>
            </div>

            {/* Running indicator */}
            {data.run.Status === 'Running' && (
              <div className="mt-4 flex items-center space-x-2 rounded-md bg-blue-50 dark:bg-blue-950 px-4 py-3 text-sm text-blue-700 dark:text-blue-300">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Process is running — this page will update automatically...</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-4 flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  void downloadExcelExport(id);
                }}
                className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
              >
                Export to Excel
              </button>
              {hasStep7 && (
                <button
                  type="button"
                  onClick={() => {
                    void downloadCollectionFile(id);
                  }}
                  className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Download Collection File
                </button>
              )}
            </div>
          </div>

          {/* Step Breakdown */}
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-primary mb-3">Step Breakdown</h2>
            <DataTable
              data={data.steps}
              columns={stepColumns}
              onRowClick={(step) =>
                setExpandedStep(expandedStep === step.StepNumber ? null : step.StepNumber)
              }
              pageSize={10}
            />
          </div>

          {/* Account Details for Selected Step */}
          {expandedStep && (
            <div className="mt-6">
              <h2 className="text-lg font-semibold text-primary mb-3">
                Step {expandedStep} — Accounts
              </h2>
              {accountsLoading && <LoadingSpinner size="sm" message="Loading accounts..." />}
              {accounts && (
                <DataTable data={accounts} columns={accountColumns} pageSize={50} />
              )}
            </div>
          )}
        </>
      )}
    </PageLayout>
  );
};
